import config from 'config';

import { FC, Fragment } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
// i18next
import { useTranslation } from 'react-i18next';
// Redux
import { useAppSelector } from 'hooks/useStore';
import { getGroupItem } from 'store/group/groupSelectors';
import MediaFormats from 'types/MediaFormats';
// Components
import { Toggle } from 'components/Controls';
import Accordion from 'components/Accordion';
import Copy from 'components/Copy';
import ImageUpload from 'components/ImageUpload';
import { ColorPicker } from 'components/ColorPicker';
// Utilities
import { isRequired } from 'utilities/validation';

const SectionLiveLeaderboard:FC = () => {
  const { t } = useTranslation();

  const groupItem = useAppSelector(getGroupItem);

  const { control, formState: { errors }, watch } = useFormContext();

  const publicLeaderboardEnabledWatcher = watch('publicLeaderboard.enabled');
  const publicTeamLeaderboardEnabledWatcher = watch('publicLeaderboard.teamEnabled');

  return (
    <div react-scroll="liveLeaderboard">
      <Accordion label={t('labels.liveLeaderboard')} icon="medal">
        <div className="w-full">
          <div className="flex items-center mb-4">
            <span className="block text-white text-base font-semibold mr-3">{t('pages.groupForm.enabledPublicLeaderboardLabel')}</span>
            <Controller
              control={control} name="publicLeaderboard.enabled"
              render={({ field: { onChange, value } }) => (
                <Toggle
                  id="publicLeaderboardEnabled"
                  checked={value}
                  onChange={onChange}
                />
              )}
            />
          </div>
          <span className="block text-gray-400 text-sm">{t('pages.groupForm.enabledPublicLeaderboardHint')}</span>
          <div className="pt-2 flex items-center gap-2 mb-4">
            <a
              rel="noreferrer" target="_blank"
              className="text-sm text-primary hover:underline"
              href={`${config.quizURL}/quiz-group/${groupItem?.slug}/live-leaderboard`}
            >
              {`${config.quizURL}/quiz-group/${groupItem?.slug}/live-leaderboard`}
            </a>
            <Copy text={`${config.quizURL}/quiz-group/${groupItem?.slug}/live-leaderboard`} />
          </div>

          <div className="mb-4">
            <div className="flex items-center mb-2">
              <span className="block text-white text-base font-semibold mr-3">{t('labels.teamLeaderboard')}</span>
              <Controller
                control={control} name="publicLeaderboard.teamEnabled"
                render={({ field: { onChange, value } }) => (
                  <Toggle
                    id="publicLeaderboardTeamEnabled"
                    checked={value}
                    onChange={onChange}
                  />
                )}
              />
            </div>
            <span className="block text-gray-400 text-sm">{t('pages.groupForm.enabledTeamLeaderboardHint')}</span>
            {publicTeamLeaderboardEnabledWatcher && (
              <div className="pt-2 flex items-center gap-2">
                <a
                  rel="noreferrer" target="_blank"
                  className="text-sm text-primary hover:underline"
                  href={`${config.quizURL}/quiz-group/${groupItem?.slug}/live-leaderboard?scope=team`}
                >
                  {`${config.quizURL}/quiz-group/${groupItem?.slug}/live-leaderboard?scope=team`}
                </a>
                <Copy text={`${config.quizURL}/quiz-group/${groupItem?.slug}/live-leaderboard?scope=team`} />
              </div>
            )}
          </div>

          {publicLeaderboardEnabledWatcher && (
            <Fragment>
              <div className="w-full flex flex-wrap gap-4">
                <div className="mb-4">
                  <Controller
                    control={control} name="publicLeaderboard.portraitWallpaperImage"
                    render={({ field }) => (
                      <ImageUpload
                        {...field}
                        format={MediaFormats.PortraitWallpaper}
                        label="labels.portraitWallpaperImage"
                      />
                    )}
                  />
                </div>
                <div className="flex-grow">
                  <Controller
                    control={control} name="publicLeaderboard.wallpaperImage"
                    render={({ field }) => (
                      <ImageUpload
                        {...field}
                        format={MediaFormats.Wallpaper}
                        label="labels.wallpaperImage"
                      />
                    )}
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-4 sm:gap-8">
                <div className="mb-4">
                  <Controller
                    control={control} name="publicLeaderboard.primaryColor"
                    rules={{ required: isRequired }}
                    render={({ field: { value, onChange } }) => (
                      <ColorPicker
                        label={t('labels.primaryColor')}
                        value={value}
                        onChange={onChange}
                        required={true}
                        error={Boolean(errors.publicLeaderboard?.primaryColor)}
                        errorText={errors.publicLeaderboard?.primaryColor ? errors.publicLeaderboard.primaryColor.message : ''}
                        helperText={t('pages.groupForm.publicLeaderboardPrimaryColorHint')}
                      />
                    )}
                  />
                </div>
                <div className="mb-4">
                  <Controller
                    control={control} name="publicLeaderboard.textColor"
                    rules={{ required: isRequired }}
                    render={({ field: { value, onChange } }) => (
                      <ColorPicker
                        label={t('labels.textColor')}
                        value={value}
                        onChange={onChange}
                        required={true}
                        error={Boolean(errors.publicLeaderboard?.textColor)}
                        errorText={errors.publicLeaderboard?.textColor ? errors.publicLeaderboard.textColor.message : ''}
                        helperText={t('pages.groupForm.publicLeaderboardTextColorHint')}
                      />
                    )}
                  />
                </div>
                <div className="mb-4">
                  <Controller
                    control={control} name="publicLeaderboard.backgroundColor"
                    rules={{ required: isRequired }}
                    render={({ field: { value, onChange } }) => (
                      <ColorPicker
                        label={t('labels.backgroundColor')}
                        value={value}
                        onChange={onChange}
                        required={true}
                        error={Boolean(errors.publicLeaderboard?.backgroundColor)}
                        errorText={errors.publicLeaderboard?.backgroundColor ? errors.publicLeaderboard.backgroundColor.message : ''}
                        helperText={t('pages.groupForm.publicLeaderboardBackgroundColorHint')}
                      />
                    )}
                  />
                </div>
              </div>
            </Fragment>
          )}
        </div>
      </Accordion>
    </div>
  )
}

export default SectionLiveLeaderboard;
